import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { BlockThemeConfig, DChartInterface, DSData, Service } from 'webapp-types';
import {
	BlockRenderStep,
	BlockVisualization,
	BlockVisualizationSpecial,
	ModelName,
} from 'webapp/enums/enums';
import { copyObject, uniqueId } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockContentNumberAndChartChartComponent from 'webapp/components/block/content/number-and-chart/chart';

import type Block from 'webapp/models-custom/block';
import type BlockRenderer from 'webapp/models-custom/block-renderer';
import type { CustomColorMap } from 'webapp/models-custom/block-setup';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';
import type { SampleData } from 'webapp/models/sample';

interface Args {
	model: DsDataOutputTopLevel;
	blockRenderer: BlockRenderer;
	themeData: BlockThemeConfig.BlockParsed;
	colorIndexOrClass: number | string;
	customColorClassMap: CustomColorMap;
	annotationContext: DChartInterface.AnnotationContext;
}

export default class BlockContentNumberAndChartChartWrapperComponent extends Component<Args> {
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;
	@service('store') declare storeService: Service.Store;

	@tracked visualization: VisualizationLineChart | null = null;

	private fakeBlock: Block | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// avoiding errors with rendering
		// bcos we are creating new block, ds, and sample records which trigger revalidation of allRecords
		next(this, this.initializeFakeBlock);
	}

	initializeFakeBlock() {
		const fakeId = -1 * uniqueId();

		const block = this.mmBlocksService.createNewModel({
			id: String(fakeId),
			visualization: BlockVisualization.LINE_CHART,
		});

		this.mmDataSelectsService.createNewModel({
			id: String(-1 * uniqueId()),
			blockId: fakeId,
			dateRange: { range: {} },
		});

		this.storeService.createRecord(ModelName.SAMPLE, {
			id: String(fakeId),
			sampledata: {},
		});

		this.fakeBlock = block; // so we can cleanup it later

		block.blockSetup.visualization.forceDChartRendererType =
			BlockVisualizationSpecial.METRIC_CHART;

		next(this, this.updateFakeRecords);

		next(() => {
			this.visualization = block.blockSetup.visualization as VisualizationLineChart;
		});
	}

	@action
	updateFakeRecords() {
		const block = this.fakeBlock;

		if (!block) {
			return;
		}

		const output = this.args.model,
			originalDs = output.dataRoot.dataSelect,
			originalBlock = originalDs.block,
			originalSampleData = originalBlock?.sample?.sampledata || {};

		if (!originalBlock) {
			return;
		}

		const sampleData = copyObject(originalSampleData) as SampleData;

		if (originalBlock?.dateRange) {
			block.dateRange = originalBlock.dateRange;
		}

		const ds = block.blockSetup.dataSelects[0];
		if (ds) {
			const dsDataItem = (sampleData.dsData || []).find((dsData: DSData.RootTop) => {
				return dsData.dsId === originalDs?.numericId;
			});
			if (dsDataItem) {
				// replace the correct data id with the fake id
				dsDataItem.dsId = ds.numericId!;
				// leave only the output that matches the original attribute & unit
				const dataItem = (dsDataItem.data || []).find((item: DSData.PointGroupTopLevel) => {
					if (
						output.equalDimensionValues(item.dimensionValues || {}) ||
						(!output.hasAttributeNames &&
							!Object.values(item.dimensionValues || {}).length)
					) {
						if (output.unit === item.unit || (!output.unit && !item.unit)) {
							return true;
						}
					}
					return false;
				});
				dsDataItem.data = dataItem ? [dataItem] : [];
			}
			sampleData.dsData = dsDataItem ? [dsDataItem] : [];
		}

		const sample = block.sample;
		if (sample) {
			sample.sampledata = sampleData;
		}
	}

	@action
	setupView(node: HTMLElement): void {
		const blockRenderer = this.args.blockRenderer;
		blockRenderer.registerStepHandler(BlockRenderStep.SINGLE_NUMBER, this, () => {
			node.querySelector('.bc__chart')?.dispatchEvent(new Event('resize')); // uniblock/chart
			return Promise.resolve();
		});

		// prepare functions for teardown, since they dont have access to this.args
		this.teardownFns.push(() => {
			blockRenderer.unregisterStepHandler(BlockRenderStep.SINGLE_NUMBER, this);
		});
	}

	private teardownFns: (() => void)[] = [];

	@action
	teardownView(): void {
		this.teardownFns.forEach((fn) => fn());
	}

	override willDestroy() {
		super.willDestroy();
		const block = this.fakeBlock;
		if (block) {
			this.garbageCollectorService.clearConnectedRecords('block', block);
			try {
				block.model.unloadRecord();
			} catch (_e) {
				// probably already unloaded (snapshooter unloadAll)
			}
		}
	}
	<template>
		<div
			class="bc__number-chart"
			{{didInsert this.setupView}}
			{{didUpdate this.updateFakeRecords @model}}
			{{willDestroy this.teardownView}}
		>
			{{#if this.visualization}}
				<BlockContentNumberAndChartChartComponent
					@visualization={{this.visualization}}
					@colorIndexOrClass={{@colorIndexOrClass}}
					@customColorClassMap={{@customColorClassMap}}
					@themeData={{@themeData}}
					@isSyncing={{@model.dataRoot.isDataIncomplete}}
					@annotationContext={{@annotationContext}}
					@canAnnotate={{true}}
				/>
			{{/if}}
		</div>
	</template>
}
